import React, { PropsWithChildren } from 'react';
import { useTheme } from '@mui/material/styles';
import { Canvas, events } from '@react-three/fiber';
import { Intersection } from 'three';

import { RaycastOrder } from '@paragon/three-shared';
import { sortBy } from '@paragon/utilities';

import { useAppDispatch } from '../components/hooks';
import { requestContextMenu } from '../store/actions/contextMenuActions';
import { resetSelectedItems } from '../store/actions/selectActions';
import { Scene } from './Scene';

interface Props extends PropsWithChildren {
  readonly canvasRef: (element: HTMLCanvasElement | null) => void;
}

export const SceneWrapper = ({ canvasRef, children }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  return (
    <Canvas
      frameloop="demand"
      dpr={window.devicePixelRatio}
      ref={canvasRef}
      events={eventManagerFactory}
      onPointerMissed={(event) => {
        if (event.button !== 2) return;
        dispatch(resetSelectedItems());
        dispatch(requestContextMenu(event, { type: null }));
      }}
    >
      <color attach="background" args={[theme.palette.canvas.main]} />
      {children}
      <Scene />
    </Canvas>
  );
};

const eventManagerFactory: Parameters<typeof Canvas>[0]['events'] = (state) => {
  const defaultEventManager = events(state);

  return {
    ...defaultEventManager,

    // We don't rely on wheel events through react-three-fiber, so no need to waste frame time on a raycast.
    //   `OrbitControls` and `ThreeJsDom` react to wheel events independently from this.
    handlers: { ...defaultEventManager.handlers!, onWheel: () => {} },

    filter: (items: Intersection[]) =>
      sortBy(
        items.filter(({ object }) => object.visible),
        ({ object }) => object.userData.raycastOrder ?? RaycastOrder.Default,
      ),
  };
};
